import { getFormProps, getInputProps, getTextareaProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import { data, Form } from "react-router";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { z } from "zod";
import { ErrorList, Field, TextareaField } from "#app/components/forms.tsx";
import { StatusButton } from "#app/components/ui/status-button.tsx";
import { prisma } from "#app/utils/db.server.ts";
import * as gtag from "#app/utils/gtags.client.ts";
import { checkHoneypot } from "#app/utils/honeypot.server.ts";
import { useIsPending } from "#app/utils/misc.tsx";
import { NotificationService } from "#app/utils/notifications.server.ts";
import { redirectWithToast } from "#app/utils/toast.server.ts";
import { type Route } from "./+types/feedback";

const FeedbackSchema = z.object({
  name: z.string().optional(),
  email: z.string().email().optional(),
  feedback: z.string().min(10, "Please provide more detailed feedback"),
});

export async function action({ request }: Route.ActionArgs) {
  const formData = await request.formData();
  await checkHoneypot(formData);

  const submission = parseWithZod(formData, { schema: FeedbackSchema });

  if (submission.status !== "success") {
    return data(
      { result: submission.reply() },
      { status: submission.status === "error" ? 400 : 200 },
    );
  }

  // Save to database
  await prisma.feedback.create({
    data: submission.value,
  });

  // Notify admin
  void NotificationService.enqueue({
    type: "feedback_received",
    payload: {
      name: submission.value.name,
      email: submission.value.email,
      feedback: submission.value.feedback,
    },
  });

  return redirectWithToast("/", {
    title: "Thank you!",
    description: "Your feedback has been well received.",
  });
}

export const meta: Route.MetaFunction = () => {
  return [{ title: "Leave Feedback | shonaslang" }];
};

export default function FeedbackPage({ actionData }: Route.ComponentProps) {
  const isPending = useIsPending();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const formData = new FormData(e.currentTarget);
    const feedback = formData.get("feedback") as string;

    gtag.event({
      action: "submit_form",
      category: "Feedback",
      label: feedback?.substring(0, 100) || "feedback_submitted", // Limit length
      value: "1",
    });
  };

  const [form, fields] = useForm({
    id: "feedback-form",
    constraint: getZodConstraint(FeedbackSchema),
    lastResult: actionData?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: FeedbackSchema });
    },
  });

  return (
    <div className="container py-16">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-4">Leave Feedback</h1>
        <p className="text-muted-foreground mb-8">
          Help us improve shonaslang! Share your thoughts, suggestions, or
          report issues.
        </p>

        <Form
          method="POST"
          {...getFormProps(form)}
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          <HoneypotInputs />

          <Field
            labelProps={{ children: "Name (optional)" }}
            inputProps={{
              ...getInputProps(fields.name, { type: "text" }),
              placeholder: "Your name",
            }}
            errors={fields.name.errors}
          />

          <Field
            labelProps={{ children: "Email (optional)" }}
            inputProps={{
              ...getInputProps(fields.email, { type: "email" }),
              placeholder: "your@email.com",
            }}
            errors={fields.email.errors}
          />

          <TextareaField
            labelProps={{ children: "Feedback" }}
            textareaProps={{
              ...getTextareaProps(fields.feedback),
              placeholder: "Tell us what you think...",
              rows: 5,
            }}
            errors={fields.feedback.errors}
          />

          <ErrorList errors={form.errors} id={form.errorId} />

          <StatusButton
            className="w-full"
            status={isPending ? "pending" : (form.status ?? "idle")}
            type="submit"
            disabled={isPending}
          >
            Send Feedback
          </StatusButton>
        </Form>
      </div>
    </div>
  );
}
